/**
 * reportClientError — fire-and-forget client-side error reporter.
 *
 * Sends a POST to /api/client-errors with the error details.
 * Never throws — if the report fails, it logs to console and moves on.
 *
 * Usage:
 *   reportClientError({ error_type: 'js_error', message: err.message, stack: err.stack })
 *
 * The reporter automatically enriches every payload with:
 *   - url + route       — from window.location
 *   - breadcrumbs       — last ~30 client-side events (lib/breadcrumbs)
 *   - ui_state          — open modals / slideouts snapshot (lib/uiState)
 *   - clarity_session_id — per-tab Clarity session ID (lib/clarity)
 *   - build_sha         — NEXT_PUBLIC_BUILD_SHA at error fire time
 *
 * Callers only need to supply error_type, message, stack, etc. — the
 * cross-cutting diagnostic context is collected here so every reporting site
 * (window.onerror, unhandledrejection, error boundaries) gets it for free.
 */

import { getBreadcrumbs } from '@/lib/breadcrumbs'
import { getUiStateSnapshot } from '@/lib/uiState'
import { getClaritySessionId, upgradeClaritySession } from '@/lib/clarity'

export interface ClientErrorPayload {
  error_type:       'js_error' | 'unhandled_rejection' | 'react_boundary' | 'network_error'
  severity?:        'error' | 'warning' | 'info'
  message:          string
  stack?:           string | null
  component_stack?: string | null
  route?:           string
  brand_id?:        string | null
  user_id?:         string | null
}

export function reportClientError(payload: ClientErrorPayload): void {
  // Skip during SSR
  if (typeof window === 'undefined') return

  // Skip from local dev — Fast Refresh / mid-edit errors are not real bugs.
  // Vercel previews on *.vercel.app are NOT blocked by this; only loopback.
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return

  // Tell Clarity to preserve this session so we can watch the replay later.
  // Safe no-op if Clarity isn't loaded.
  upgradeClaritySession(`error:${payload.error_type}`)

  const enriched = {
    ...payload,
    url:                window.location.href,
    route:              payload.route ?? window.location.pathname,
    breadcrumbs:        getBreadcrumbs(),
    ui_state:           getUiStateSnapshot(),
    clarity_session_id: getClaritySessionId(),
    build_sha:          process.env.NEXT_PUBLIC_BUILD_SHA || null,
  }

  // Fire-and-forget — don't await, don't throw
  fetch('/api/client-errors', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify(enriched),
    // Use keepalive so the request survives page unloads
    keepalive: true,
  }).catch(() => {
    // Silently ignore — we never want the error reporter to cause more errors
  })
}
